import type { ReactNode } from 'react';
import { Save, X } from 'lucide-react';
import { Button, Stack } from '../../atoms';
import { ListCard, ListCardRow, StatusBlock } from '../../molecules';
import { PageContainer, PageHeader } from '../../templates';

export interface CerAreaViewProps {
	nome: string;
	fiscal: ReactNode;
	branding: ReactNode;
	features?: ReactNode;
	/** Amministratori della CER — solo per il superadmin. */
	admins?: ReactNode;
	/**
	 * Salva dati della CER e parametri delle configurazioni insieme. Branding e
	 * funzionalità hanno il proprio pulsante: scrivono su tabelle diverse e
	 * valgono da sole (INB-374).
	 */
	onSave: () => void;
	saving: boolean;
	onCancel: () => void;
}

/** One community editor, with sections in reading order and no internal navigation. */
export function CerAreaView({
	nome,
	fiscal,
	branding,
	features,
	admins,
	onSave,
	saving,
	onCancel,
}: CerAreaViewProps) {
	return (
		<PageContainer width="md">
			<section aria-label="Dettaglio CER" className="space-y-8">
				<PageHeader
					title={nome || 'CER'}
					size="medium"
					stackOnMobile
					actions={
						<>
							<Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
								<X />
								Annulla
							</Button>
							<Button type="button" variant="accent" onClick={onSave} disabled={saving}>
								<Save />
								{saving ? 'Salvataggio...' : 'Salva'}
							</Button>
						</>
					}
				/>
				<section aria-labelledby="cer-fiscal-heading" className="space-y-4">
					<h2 id="cer-fiscal-heading" className="text-lg font-semibold text-text">
						Dati della CER
					</h2>
					{fiscal}
				</section>
				{admins}
				<section aria-labelledby="cer-branding-heading" className="space-y-4">
					<h2 id="cer-branding-heading" className="text-lg font-semibold text-text">
						Personalizzazione
					</h2>
					{branding}
				</section>
				{features && (
					<section aria-labelledby="cer-features-heading" className="space-y-4">
						<h2 id="cer-features-heading" className="text-lg font-semibold text-text">
							Funzionalità
						</h2>
						{features}
					</section>
				)}
			</section>
		</PageContainer>
	);
}

export interface CerListItem {
	id: string;
	nome: string;
}
export interface CerListViewProps {
	cers: CerListItem[];
	loading: boolean;
	onOpen: (id: string) => void;
}

export function CerListView({ cers, loading, onOpen }: CerListViewProps) {
	if (loading) return <StatusBlock kind="loading" size="lg" message="Caricamento CER..." />;
	return (
		<PageContainer width="narrow">
			<Stack gap="lg">
				<PageHeader
					title="CER"
					size="medium"
					description="Le comunità energetiche che amministri."
				/>
				{cers.length === 0 ? (
					<StatusBlock kind="empty" message="Non amministri ancora nessuna CER." />
				) : (
					<ListCard header="Comunità energetiche">
						{cers.map((cer) => (
							<ListCardRow
								key={cer.id}
								title={cer.nome}
								actions={
									<Button
										variant="outline"
										size="sm"
										onClick={() => onOpen(cer.id)}
										aria-label={`Apri ${cer.nome}`}
									>
										Dettagli
									</Button>
								}
							/>
						))}
					</ListCard>
				)}
			</Stack>
		</PageContainer>
	);
}
